import _ from "lodash";
import { useEffect, useRef } from "react";
import propTypes from "prop-types";

// Internal
import {
  navigateThroughArrowKeys,
  updateTabIndexRecursively,
} from "Utils/AccessibilityHelpers";
import { useDynamicColumns, useId } from "Hooks";

const getFirstFocusableListItem = ({ listId, listItemRole }) =>
  `[data-list-id="${listId}"] > [role="${listItemRole}"]:not([aria-disabled="true"])`;
const getCurrentFocusableListItem = ({ listId, listItemRole }) =>
  `[data-list-id="${listId}"] > [role="${listItemRole}"][tabindex="0"]`;

// This is recursive function to find the next valid sibling
const getNextValidSibling = ({ sibling, isNext }) => {
  if (_.isNil(sibling)) return null;

  return !_.isEqual("true", sibling?.getAttribute("aria-disabled"))
    ? sibling
    : getNextValidSibling({
        sibling: isNext ? sibling?.nextSibling : sibling?.previousSibling,
        isNext,
      });
};

const initListItems = ({ listItems, listItemRole }) => {
  _.forEach(listItems, item => {
    item?.setAttribute("role", listItemRole);
    updateTabIndexRecursively({ element: item, focusable: false });
  });
};

// This function will handle the mutation and update the tabindex and role of the added nodes. In case nodes are removed, this will find the next valid sibling and focus on it
const handleMutation = ({
  container,
  listId,
  listItemRole,
  autoFocusItemOnListChange,
}) => mutation => {
  const {
    addedNodes = [],
    removedNodes = [],
    nextSibling = null,
    previousSibling = null,
  } = mutation;

  // If some nodes are added, update the tabindex and role of the added nodes
  if (!_.isEmpty(addedNodes)) {
    initListItems({ listItems: addedNodes, listItemRole });
  }

  // If current focussed element is removed from the list, focus on the next valid sibling
  if (!_.isEmpty(removedNodes)) {
    // Grab the current focusable element
    const currentFocusableElement = container?.querySelector(
      getCurrentFocusableListItem({ listId, listItemRole })
    );

    // If there is a current focusable element, then no need to focus on the next sibling
    if (currentFocusableElement) return;

    const nextValidSibling = getNextValidSibling({
      sibling: nextSibling,
      isNext: true,
    });
    const previousValidSibling = getNextValidSibling({
      sibling: previousSibling,
      isNext: false,
    });

    const siblingToFocus = nextValidSibling
      ? nextValidSibling
      : previousValidSibling;

    if (siblingToFocus) {
      updateTabIndexRecursively({
        element: siblingToFocus,
        focusable: true,
      });
      if (autoFocusItemOnListChange) {
        siblingToFocus?.focus();
      }
    }
  }
};

// This function checks if there is any focusable element in the container, if not, then it updates the tabIndex of the first list item
const updateTabIndex = ({ container, listId, listItemRole }) => {
  const firstFocusableElement = container?.querySelector(
    getFirstFocusableListItem({ listId, listItemRole })
  );
  const currentFocusableElement = container?.querySelector(
    getCurrentFocusableListItem({ listId, listItemRole })
  );

  if (_.isNil(currentFocusableElement) && !_.isNil(firstFocusableElement)) {
    updateTabIndexRecursively({
      element: firstFocusableElement,
      focusable: true,
    });
  }
};

/**
 * A component that provides navigation through arrow keys for a list of items.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.RefObject} props.listContainerRef - A ref to the list container element.
 * @param {React.ReactNode} props.children - The child elements to be rendered inside the list.
 * @param {boolean} [props.shouldPreventDefault=false] - Whether to prevent the default browser behavior, i.e scrolling.
 * @param {string} [props.listRole="list"] - The role of the list container.
 * @param {string} [props.listItemRole="listitem"] - The role of the list items.
 * @param {boolean} [props.autoFocusItemOnListChange=true] - Whether the item should be focused when list items are mutated.
 *
 * @returns {React.ReactNode} The rendered children with keyboard navigation enabled.
 *
 * @example
 * const listContainerRef = useRef(null);
 *
 * <KeyboardNavigableList listContainerRef={listContainerRef} shouldPreventDefault={true} listRole="listbox" listItemRole="button">
 *    <div ref={listContainerRef}>
 *      <div>Item 1</div>
 *      <div aria-disabled="true">Item 2</div> // Focus will not go on this item
 *      <div>Item 3</div>
 *    </div>
 * </KeyboardNavigableList>
 */
const KeyboardNavigableList = props => {
  const {
    listContainerRef,
    children,
    shouldPreventDefault = false,
    listRole = "list",
    listItemRole = "listitem",
    autoFocusItemOnListChange = true,
  } = props;

  const id = useId();
  const columns = useDynamicColumns(listContainerRef);
  const listItems = useRef([]);

  // This effect will run only once and will set up mutation observer and roles/tabIndex on list and listitems
  useEffect(() => {
    const container = listContainerRef?.current;
    if (_.isNil(container)) {
      console.error("List container ref is not provided.");
      return;
    }

    container?.setAttribute("role", listRole);
    container?.setAttribute("data-list-id", id);
    listItems.current = _.toArray(container?.children);

    // Initialize the list items
    initListItems({ listItems: listItems.current, listItemRole });

    // Update the tabIndex of first list item to zero
    updateTabIndex({ container, listId: id, listItemRole });

    const handleMutations = mutations => {
      const [addedMutations, removedMutations] = _.partition(
        mutations,
        mutation => mutation.addedNodes.length > 0
      );

      // Handle the added nodes
      _.forEach(
        addedMutations,
        handleMutation({
          container,
          listId: id,
          listItemRole,
          autoFocusItemOnListChange,
        })
      );

      // Handle just the last removed node
      if (!_.isEmpty(removedMutations)) {
        handleMutation({
          container,
          listId: id,
          listItemRole,
          autoFocusItemOnListChange,
        })(_.last(removedMutations));
      }

      // Update the list items
      listItems.current = _.toArray(container?.children);

      // If for some reason, there is no focusable element in the container, then update the tabIndex of the first list item
      updateTabIndex({ container, listId: id, listItemRole });
    };

    // Mutation observer for handling item removal
    const observer = new MutationObserver(handleMutations);
    observer.observe(container, { childList: true });

    return () => {
      if (observer) observer.disconnect();
    };
  }, [id, listContainerRef, listItemRole, listRole, autoFocusItemOnListChange]);

  // This effect will run once during mount and second, if the number of columns changes(like user resized the browser)
  useEffect(() => {
    const container = listContainerRef?.current;
    if (_.isNil(container)) {
      console.error("List container ref is not provided.");
      return;
    }
    const handleKeyDown = event => {
      if (
        _.includes(
          ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"],
          event.key
        )
      ) {
        event?.stopPropagation();
        const currentFocusableElement = container?.querySelector(
          getCurrentFocusableListItem({ listId: id, listItemRole })
        );

        navigateThroughArrowKeys({
          event,
          element: currentFocusableElement,
          shouldPreventDefault,
          columns,
          listItems: listItems.current,
        });
      }
    };

    container?.addEventListener("keydown", handleKeyDown);

    return () => {
      if (container) {
        container?.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [columns, id, listContainerRef, listItemRole, shouldPreventDefault]);

  return children;
};

KeyboardNavigableList.propTypes = {
  listContainerRef: propTypes.object.isRequired,
  children: propTypes.node.isRequired,
  shouldPreventDefault: propTypes.bool,
  listRole: propTypes.string,
  listItemRole: propTypes.string,
  autoFocusItemOnListChange: propTypes.bool,
};

export { KeyboardNavigableList };

KeyboardNavigableList.displayName = "KeyboardNavigableList";
